import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Loader, EmptyState } from "@/components/common";
import { Check, Zap, Coins, Sparkles, Infinity as InfinityIcon } from "lucide-react";

export default function Pricing() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/billing").then((r) => setData(r.data)).catch(() => setErr(true)); }, []);

  const buy = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const r = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy("");
    }
  };

  if (err) return <div className="p-6 lg:p-10" data-testid="pricing-error"><EmptyState icon={Coins} title="Couldn't load plans" hint="Check your connection and refresh the page." /></div>;
  if (!data) return <div className="p-6 lg:p-10"><Loader label="Loading plans" /></div>;

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <PageHeader eyebrow="Upgrade" title="Fuel Your Channel" testid="pricing-header"
        subtitle="Each AI video costs 1 credit. Buy a pack, or go Pro for unlimited videos + autopilot." />

      <div className="flex items-center gap-4 mb-8 fade-up">
        <div className="bg-surface border border-white/10 rounded-md px-5 py-3 flex items-center gap-3" data-testid="credits-balance">
          <Coins className="w-5 h-5 text-signal-yellow" />
          <div>
            <div className="font-mono text-2xl leading-none">{data.pro_active ? "∞" : data.credits}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/40 font-mono mt-1">{data.pro_active ? "Pro · unlimited" : "credits left"}</div>
          </div>
        </div>
        {data.pro_active && <span className="text-sm text-signal-green font-mono">Pro plan active ✓</span>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 fade-up">
        {data.plans.map((p) => {
          const isPro = p.type === "subscription";
          return (
            <div key={p.lookup_key} data-testid={`plan-${p.lookup_key}`}
              className={`relative bg-surface border rounded-md p-6 flex flex-col ${p.highlight ? "border-brand" : isPro ? "border-signal-yellow/40" : "border-white/10"} hover:-translate-y-1 transition-transform duration-200`}>
              {p.highlight && <span className="absolute -top-2.5 left-6 bg-brand text-white text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded">Popular</span>}
              {isPro && <span className="absolute -top-2.5 left-6 bg-signal-yellow text-black text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded">Best for scale</span>}
              <div className="flex items-center gap-2 mb-1">
                {isPro ? <Sparkles className="w-4 h-4 text-signal-yellow" /> : <Coins className="w-4 h-4 text-white/40" />}
                <h3 className="font-heading text-xl">{p.name}</h3>
              </div>
              <div className="flex items-end gap-1 my-3">
                <span className="font-mono text-4xl">${p.price}</span>
                <span className="text-white/40 text-sm mb-1">{isPro ? "/mo" : "once"}</span>
              </div>
              <p className="text-sm text-white/50 mb-4">{p.blurb}</p>
              <div className="space-y-2 mb-6 flex-1">
                <div className="flex items-center gap-2 text-sm text-white/70">
                  {isPro ? <InfinityIcon className="w-4 h-4 text-signal-green" /> : <Check className="w-4 h-4 text-signal-green" />}
                  {isPro ? "Unlimited AI videos" : `${p.credits} video credits`}
                </div>
                <div className="flex items-center gap-2 text-sm text-white/70"><Check className="w-4 h-4 text-signal-green" /> Scripts, thumbnails & variants</div>
                {isPro && <div className="flex items-center gap-2 text-sm text-white/70"><Check className="w-4 h-4 text-signal-green" /> Autopilot auto-production</div>}
                {!isPro && <div className="flex items-center gap-2 text-sm text-white/70"><Check className="w-4 h-4 text-signal-green" /> Credits never expire</div>}
              </div>
              <button data-testid={`buy-${p.lookup_key}`} onClick={() => buy(p.lookup_key)} disabled={busy === p.lookup_key}
                className={`w-full rounded-md px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${p.highlight || isPro ? "bg-brand hover:bg-brand-hover text-white" : "bg-white/10 hover:bg-white/15 text-white"}`}>
                <Zap className="w-4 h-4" /> {busy === p.lookup_key ? "Redirecting..." : isPro ? "Go Pro" : "Buy Credits"}
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-white/30 mt-6 font-mono">Secure checkout via Stripe (sandbox). Test mode — use card 4242 4242 4242 4242, any future date & CVC.</p>
    </div>
  );
}
